import React from "react";

import EventEmitter from "events";

import { encodeNoteLocation, OriginatingNoteState } from "~/actionItems/locationState";
import Cache from "~/lib/cache";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";

interface State {
	url?: string;
	title?: string;
	noteId?: string;
	blockKey?: string;
}

let socket: GroupEmitter | undefined;

export const jumpToBlockEmitter = new EventEmitter();

function getSocket() {
	if (socket) {
		return socket;
	}
	return (socket = WebsocketClient.getInstance().getOrJoinGroup(`session_generic.${window.CLIENT_SESSION_ID}`));
}

function getBlockInfo(socket: GroupEmitter, originalBlockId: string, remainingTries = 3): Promise<Partial<State>> {
	return socket
		.request<Partial<State>>({
			type: "getUrlForBlock",
			originalBlockId,
		})
		.catch(e => {
			if (remainingTries > 0) {
				return getBlockInfo(socket, originalBlockId, remainingTries - 1);
			}
			throw new Error(`Failed to get info for block ${originalBlockId}: ${e}`);
		});
}

export const cache = new Cache<string, Partial<State>>(5 * 60 * 1000 /* 5 minutes */, originalBlockId =>
	getBlockInfo(getSocket(), originalBlockId),
);

function navigateToBlock(info: State, originatingNoteId: string, newTab = false): void {
	const { url, noteId, blockKey } = info;
	if (!(url && noteId && blockKey)) return;

	jumpToBlockEmitter.emit("jumpToBlock");

	if (newTab) {
		window.open(url, "_blank", "noopener");
	} else {
		window.HISTORY.push({
			pathname: url,
			hash: encodeNoteLocation({ noteId, blockKey }),
			state: new OriginatingNoteState(originatingNoteId),
		});
	}
}

export function jumpToOriginalBlock(
	originalBlockId: string,
	originatingNoteId: string,
	newTab?: boolean,
): Promise<void> {
	return cache.get(originalBlockId).then(state => navigateToBlock(state, originatingNoteId, newTab));
}

export default function useActionItemJump(
	originalBlockId: string | undefined,
	originatingNoteId: string,
	forceNewTab = false,
) {
	return React.useCallback(
		(e: React.MouseEvent) => {
			if (!originalBlockId) return;
			jumpToOriginalBlock(originalBlockId, originatingNoteId, e.metaKey || forceNewTab);
		},
		[originalBlockId, originatingNoteId, forceNewTab],
	);
}
